/**
 * Input Component
 * Text input with validation, labels, and icons
 */

import { forwardRef, useId } from 'react';
import type { InputProps } from '../types/components';
import { cn } from '../utils/cn';

const sizeStyles: Record<string, string> = {
  sm: 'px-2.5 py-1.5 text-sm',
  md: 'px-3 py-2 text-base',
  lg: 'px-4 py-2.5 text-lg',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      size = 'md',
      type = 'text',
      disabled,
      id: providedId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const hasError = Boolean(error);

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-300"
          >
            {label}
            {props.required && <span className="text-gray-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 dark:text-slate-500">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={id}
            type={type}
            disabled={disabled}
            className={cn(
              'block w-full rounded border bg-white transition-colors dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500',
              'focus:outline-none focus:ring-2 focus:ring-offset-0 dark:focus:ring-slate-400 dark:focus:border-slate-400',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-slate-800 dark:disabled:text-slate-500',
              hasError
                ? 'border-gray-400 focus:border-gray-900 focus:ring-gray-200 dark:border-red-500/70'
                : 'border-gray-300 focus:border-gray-900 focus:ring-gray-100 dark:border-slate-700',
              sizeStyles[size],
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            aria-invalid={hasError}
            aria-describedby={
              hasError ? `${id}-error` : hint ? `${id}-hint` : undefined
            }
            {...props}
          />

          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400 dark:text-slate-500">
              {rightIcon}
            </div>
          )}
        </div>

        {error && (
          <p id={`${id}-error`} className="mt-1 text-sm text-gray-500 dark:text-red-400" role="alert">
            {error}
          </p>
        )}

        {hint && !error && (
          <p id={`${id}-hint`} className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
